import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@hero/context';
import type {
  ContractRouteRef,
  Order,
  OrderContractElement,
} from '@hero/schema';
import type { RouteGetAllAPI } from '@hero/api';
import {
  buildRouteCreatePayload,
  buildRouteCreatePayloadAsync,
  createRouteRegionSearchFn,
  findMatchingExistingRoute,
  getRouteRegionFromOrderDestination,
  routeAsContractRouteRef,
} from './buildRouteCreatePayload';

export type ExistingRouteForLane = RouteGetAllAPI['data'][number];

/**
 * Stable lookup-only cache key for the per-lane "is there already a
 * Route for this seller/buyer/vehicle + dest pair?" probe. Prefixed
 * with `route` so the platform-wide route entity invalidation (see
 * `ENTITY_CONFIGS.route.queryKeyPatterns`) busts this cache when any
 * Route mutation lands — that way creating / updating a Route via
 * the price-edit flow makes the lane probe re-fetch the next time
 * the drawer opens.
 */
const buildLookupQueryKey = (args: {
  sellerId?: string;
  buyerId?: string;
  vehicle?: string;
  dest0?: string;
  destN?: string;
}): readonly unknown[] => [
  'route',
  'for-lane',
  args.sellerId ?? null,
  args.buyerId ?? null,
  args.vehicle ?? null,
  args.dest0 ?? null,
  args.destN ?? null,
];

export interface UseExistingRouteForLaneArgs {
  order: Order | null | undefined;
  leg: OrderContractElement | undefined;
  /**
   * Gate the network call to "the drawer / sheet is open". Off-screen
   * order rows shouldn't probe — that would prefetch a row per visible
   * order on the table just to learn whether the route prompt is
   * "save" or "update".
   */
  enabled: boolean;
}

export interface UseExistingRouteForLaneResult {
  /**
   * The Route the backend already has for this lane between this leg's
   * (seller, buyer) on this vehicle + destinations. `undefined` when
   * no route exists, the lookup is still resolving, or we don't have
   * enough info to probe (missing parties, missing destinations, etc.).
   */
  existingRoute: ExistingRouteForLane | undefined;
  /**
   * The "effective" route ref the price-edit UI should branch on —
   * `leg.route` if present (the order was priced from a route at
   * create time), otherwise a projection of {@link existingRoute}
   * (the order wasn't, but a route exists for this lane today so the
   * UI should still offer "Update saved price" instead of "Save as
   * route"). `undefined` when neither is available.
   */
  effectiveRouteRef: ContractRouteRef | undefined;
  /** True while the lookup probe is in flight for an enabled key. */
  isLoading: boolean;
  /** True once the lookup has resolved (success or no-match). */
  isFetched: boolean;
}

/**
 * Resolve whether a Route already exists for the lane on this order's
 * viewer leg — independent of whether the leg's frozen
 * `ContractRouteRef` snapshot is set.
 *
 * Why this exists:
 *
 *   - `leg.route` is a **point-in-time snapshot** captured at order
 *     create. An order created with a one-time / unresolved price
 *     leaves `leg.route` empty, even if the same two parties later
 *     create a Route for that lane.
 *   - The price-edit drawer (mobile sheet + web drawer) uses that
 *     same emptiness to decide between "Save as Route" and "Update
 *     Route" prompts — which means re-opening the drawer on an old
 *     one-time order keeps offering "Save as Route" even after the
 *     user already saved one. That triggers the backend's
 *     `route_vehicle_overlap` guard and reads to the user as "Save
 *     route failed".
 *
 * This hook bridges that gap: it probes
 * `routes.getAll({ sellerId, buyerId, vehicle })`, filters
 * destinations client-side, and returns the canonical
 * {@link ContractRouteRef} the UI can branch on. The probe is
 * gated by `enabled` so it only fires when the drawer is actually
 * open — table rows don't pay the cost.
 *
 * The save-time {@link findMatchingExistingRoute} fallback inside
 * the manager remains a safety net (handles the race where a Route
 * lands between open and Save), but with this hook the UI is
 * accurate from the first paint instead of relying on the
 * post-save toast to surface that the route already existed.
 */
export const useExistingRouteForLane = (
  args: UseExistingRouteForLaneArgs,
): UseExistingRouteForLaneResult => {
  const { order, leg, enabled } = args;
  const api = useApi();

  // Cache key derives from the lane identity, NOT the order — every
  // order on the same lane re-uses the same probe.
  const sellerId = leg?.seller?._id ? String(leg.seller._id) : undefined;
  const buyerId = leg?.buyer?._id ? String(leg.buyer._id) : undefined;
  const vehicle = order?.vehicle?.classification?.type ?? order?.load?.vehicles?.[0];
  const dests = order?.destinations ?? [];
  const firstDestRegionId = useMemo(() => {
    const r = dests[0] ? getRouteRegionFromOrderDestination(dests[0]) : undefined;
    return r?._id ? String(r._id) : undefined;
  }, [dests]);
  const lastDestRegionId = useMemo(() => {
    const last = dests[dests.length - 1];
    const r = last ? getRouteRegionFromOrderDestination(last) : undefined;
    return r?._id ? String(r._id) : undefined;
  }, [dests]);

  const hasMinimumIdentity =
    !!order &&
    !!leg &&
    !!vehicle &&
    (!!sellerId || !!buyerId) &&
    dests.length >= 2;

  const queryKey = buildLookupQueryKey({
    sellerId,
    buyerId,
    vehicle: vehicle as string | undefined,
    dest0: firstDestRegionId,
    destN: lastDestRegionId,
  });

  const queryFn = async (): Promise<ExistingRouteForLane | null> => {
    if (!order || !leg) return null;
    // The price the lookup quotes is unused — we only need the
    // payload to derive (sellerId, buyerId, vehicle, destinations).
    // Region resolution may still need the async fallback when the
    // destination only carries a city name (no Region `_id`).
    //
    // The inline adapter narrows the SDK's `common.region.*` and
    // `routes.getAll` surface to the small shapes the helpers
    // expect. We can't pass `api` directly under `strictFunctionTypes`
    // because the SDK's `search` request type carries optional
    // discriminators (`types`, `withinRegionCustomCode`) the helper
    // shape doesn't declare.
    const sdkAdapter = {
      common: {
        region: {
          // SDK's `region.search` requires `limit: number` (zod applies
          // the default at parse time); seed it to a safe small page so
          // the lookup probe doesn't paginate.
          search: (req: { q: string; limit?: number }) =>
            api.common.region.search({ q: req.q, limit: req.limit ?? 5 }),
          // `region.getAllCities` takes no input (`undefined` in the
          // API contract) — the SDK's call signature still requires
          // the explicit argument under strict typing.
          getAllCities: () => api.common.region.getAllCities(undefined),
        },
      },
      routes: {
        getAll: api.routes.getAll,
      },
    };
    const searchRegion = createRouteRegionSearchFn(sdkAdapter);
    try {
      const syncPayload = buildRouteCreatePayload({
        order,
        leg,
        price: 0,
        currency: leg.amount?.currency ?? 'SAR',
      });
      const probePayload =
        syncPayload ??
        (await buildRouteCreatePayloadAsync(
          { order, leg, price: 0, currency: leg.amount?.currency ?? 'SAR' },
          searchRegion,
        ));
      if (!probePayload) return null;
      const match = await findMatchingExistingRoute(sdkAdapter, probePayload);
      return match ?? null;
    } catch (err) {
      throw err;
    }
  };

  const query = useQuery<ExistingRouteForLane | null>({
    queryKey: [...queryKey],
    queryFn,
    enabled: enabled && hasMinimumIdentity,
    staleTime: 30_000,
  });

  const existingRoute = query.data ?? undefined;
  const effectiveRouteRef = leg?.route ?? routeAsContractRouteRef(existingRoute);

  return {
    existingRoute,
    effectiveRouteRef,
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    isFetched: query.isFetched,
  };
};
